import { useState, useEffect } from 'react'
import { 
  Container, 
  useToast,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Box
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import DataTable from '../../../common/DataTable/DataTable'
import apiClient from '../../../../utils/apiClient'

const ContentModel = () => {
  const [models, setModels] = useState([])
  const [filteredModels, setFilteredModels] = useState([])
  const [loading, setLoading] = useState(true)
  const toast = useToast()
  const navigate = useNavigate()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Enhanced columns with better formatting
  const columns = [
    { 
      key: 'name', 
      label: 'Model Name',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{value}</Text>
          <Text fontSize="xs" color="gray.500">ID: #{row.id}</Text>
        </VStack>
      )
    },
    { 
      key: 'description', 
      label: 'Description',
      render: (value) => (
        <Text fontSize="sm" maxW="300px" noOfLines={2}>
          {value || 'No description provided'}
        </Text>
      )
    },
    { 
      key: 'fields', 
      label: 'Fields',
      render: (value) => {
        const fieldCount = Array.isArray(value) ? value.length : 
                          (typeof value === 'string' ? JSON.parse(value || '[]').length : 0);
        return (
          <Badge colorScheme="blue" variant="subtle">
            {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
          </Badge>
        )
      }
    },
    { 
      key: 'updated_at', 
      label: 'Last Modified',
      render: (value) => (
        <Text fontSize="sm" color="gray.600">
          {value ? new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'N/A'}
        </Text>
      )
    }
  ]

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]

  const fetchModels = async () => {
    try {
      const data = await apiClient(`/content-models/${tenantId}`)
      setModels(data)
      setFilteredModels(data)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch content models',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchModels()
  }, [])

  const handleSearch = (searchTerm) => {
    const lowercaseSearch = searchTerm.toLowerCase()
    const filtered = models.filter(model =>
      model.name.toLowerCase().includes(lowercaseSearch) ||
      (model.description && model.description.toLowerCase().includes(lowercaseSearch))
    )
    setFilteredModels(filtered)
  }

  const handleEdit = (model) => {
    navigate(`/content-models/edit/${model.id}`)
  }

  const handleDelete = async (model) => {
    try {
      const response = await apiClient(`/content-models/${tenantId}/${model.id}`, {
        method: 'DELETE'
      })

      toast({
        title: 'Success',
        description: 'Content model deleted successfully',
        status: 'success',
        duration: 3000
      })

      fetchModels()
      return response.data
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete content model',
        status: 'error',
        duration: 3000
      })
    }
  }

  const handleCreate = () => {
    navigate(`/content-models/create`)
  }

  // Calculate statistics
  const getModelStats = () => {
    const total = models.length
    const totalFields = models.reduce((acc, model) => {
      const fieldCount = Array.isArray(model.fields) ? model.fields.length : 
                        (typeof model.fields === 'string' ? JSON.parse(model.fields || '[]').length : 0);
      return acc + fieldCount;
    }, 0)
    const avgFields = total > 0 ? Math.round(totalFields / total) : 0
    
    return { total, totalFields, avgFields }
  }

  const stats = getModelStats()

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/content-types">Content Types</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Content Models</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <Heading size="lg" mb={2}>Content Models</Heading>
          <Text color="gray.500">
            Define the structure and fields for your content types. Create reusable content schemas.
          </Text>
        </Box>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Models</StatLabel>
                <StatNumber color="blue.500">{stats.total}</StatNumber>
                <StatHelpText>Content model definitions</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Fields</StatLabel>
                <StatNumber color="green.500">{stats.totalFields}</StatNumber>
                <StatHelpText>Across all models</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Avg Fields</StatLabel>
                <StatNumber color="purple.500">{stats.avgFields}</StatNumber>
                <StatHelpText>Per content model</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Enhanced DataTable */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            <DataTable
              columns={columns}
              data={filteredModels}
              onSearch={handleSearch}
              onEdit={handleEdit}
              onCreate={handleCreate}
              onDelete={handleDelete}
              searchPlaceholder="Search content models by name or description..."
              isLoading={loading}
              title="Content Models Management"
              createButtonText="Create New Model"
              emptyStateTitle="No content models found"
              emptyStateDescription={
                filteredModels.length === 0 && models.length > 0
                  ? "No content models match your search criteria. Try adjusting your search terms."
                  : "Get started by creating your first content model to define your content structure."
              }
              loadingText="Loading content models..."
            />
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default ContentModel
